import { useEffect } from 'react';
import { useToggle } from 'ahooks';
import { AnimatePresence, motion } from 'framer-motion';
import { useStyles, Button, pxToRem, Flex } from '@archipro-design/aria';
import type { ButtonProps } from '@archipro-design/aria';
import {
    CheckboxBlankLine,
    CheckboxFill,
    Filter,
    SearchLine,
} from '@archipro-design/icons';
import { cn } from '@rocco/utils/cn';
import { InPageSearchBar } from '@modules/search/components/in-page-search-bar/InPageSearchBar';
import FilterDrawer from '@modules/search/components/filter-drawer/FilterDrawer';
import type { SearchIndex } from '@modules/search/type';
import type { FilterBarCheckboxFilter } from '~/modules/search/type/filter-state';
import { useMatchesHandleData } from '~/modules/root/hook';
import { useProfessionalTabSearch } from '~/modules/professional/hook/use-professional-tab-search';
import { useCurrentTabPath } from '~/modules/professional/hook/use-current-tab-path';
import { SHOP_FILTER } from '~/modules/product/config/product-filter-config';
import * as S from './SearchFilter.style';
import { ProfessionalFilterSheet } from './ProfessionalFilterSheet';
import { RefineIcon } from './RefineIcon';
import type { SearchFilterProp } from './SearchFilter';

/**
 * Mobile filter bar for the professional grid tabs. The revamp presentation is
 * opt-in via `searchBarVariant="revamp"` (the detail-page sections pass it), so
 * the legacy tabs keep the bar they have always had while
 * `details_revamp_2026` is off.
 */
export const SearchFilterMobile: React.FC<SearchFilterProp> = (props) =>
    props.searchBarVariant === 'revamp' ? (
        <RevampFilterBar {...props} />
    ) : (
        <LegacyFilterBar {...props} />
    );

/**
 * Compact revamp bar: an icon-only "Refine" circle (opens
 * {@link ProfessionalFilterSheet}, with an applied-count badge) beside an
 * always-expanded pill search. Category, sort and every facet live inside the
 * Refine sheet, so the bar stays to a single clean row. `searchBarTheme="dark"`
 * inverts it for the dark Projects tab.
 */
const RevampFilterBar: React.FC<SearchFilterProp> = ({
    searchFilters,
    searchBarTheme = 'light',
    searchPlaceholder = 'Search',
}) => {
    const tabSearch = useProfessionalTabSearch();
    const isDark = searchBarTheme === 'dark';
    const [filterDrawerOpen, { set: setFilterDrawerState }] = useToggle(false);

    const {
        filters,
        filterStates,
        ignoreFilterKeys,
        resetFilters,
        appliedFilters,
    } = searchFilters;

    const tabIndex = useMatchesHandleData<SearchIndex>('category', 'product');
    // Active-filter count for the Refine badge. `appliedFilters` already counts
    // facet filters and the selected category (via getAppliedFilterItems),
    // mirroring the category page — getAppliedFilterNumber skipped facet arrays.
    const appliedCount = appliedFilters?.length ?? 0;

    return (
        <>
            <div className="flex items-center gap-2">
                {/* Refine circle — opens the bottom sheet. */}
                <button
                    type="button"
                    aria-label="Refine"
                    onClick={() => setFilterDrawerState(true)}
                    className={cn(
                        'relative flex size-13 shrink-0 items-center justify-center rounded-full transition-colors',
                        isDark
                            ? 'bg-white text-[#181818]'
                            : 'bg-[#181818] text-white',
                    )}
                >
                    <RefineIcon />
                    {appliedCount > 0 ? (
                        <span
                            className={cn(
                                'absolute -top-0.5 -right-0.5 flex size-5 items-center justify-center rounded-full text-[0.6875rem] font-medium',
                                isDark
                                    ? 'bg-[#181818] text-white'
                                    : 'bg-white text-[#181818]',
                            )}
                        >
                            {appliedCount}
                        </span>
                    ) : null}
                </button>

                {/* Always-expanded pill search. */}
                <div className="min-w-0 flex-1">
                    <InPageSearchBar
                        {...tabSearch}
                        variant="revamp"
                        theme={searchBarTheme}
                        placeholder={searchPlaceholder}
                    />
                </div>
            </div>

            <ProfessionalFilterSheet
                open={filterDrawerOpen}
                onOpenChange={(next) => setFilterDrawerState(next)}
                filterSections={filters ?? []}
                filterStates={filterStates}
                category={tabIndex}
                onApplyFilters={(data) => {
                    setFilterDrawerState(false);
                    resetFilters(data);
                }}
                ignoreFilterKeys={ignoreFilterKeys}
            />
        </>
    );
};

/**
 * The long-standing mobile bar: "Filters" and "Search" text buttons that open
 * the accordion {@link FilterDrawer} and a collapsible search drawer, plus the
 * inline "Shop" checkbox on tabs that expose it.
 */
const LegacyFilterBar: React.FC<SearchFilterProp> = ({ searchFilters }) => {
    const currentTabPath = useCurrentTabPath();
    const { css } = useStyles({ currentTabPath });
    const tabSearch = useProfessionalTabSearch();
    const category = useMatchesHandleData<SearchIndex>('category', 'project');
    const [filterDrawerOpen, { set: setFilterDrawerState }] = useToggle(false);
    const [
        searchDrawerOpen,
        { toggle: toggleSearchDrawer, set: setSearchDrawer },
    ] = useToggle(false);
    const {
        filters,
        filterStates,
        filterBarFilters,
        ignoreFilterKeys,
        resetFilters,
        updateFilter,
    } = searchFilters;

    const shopFilter: FilterBarCheckboxFilter | undefined =
        filterBarFilters?.find((f) => f.filterKey === SHOP_FILTER.filterKey);

    // Open search drawer if there is a default keyword
    useEffect(() => {
        if (tabSearch.defaultKeyword) {
            setSearchDrawer(true);
        }
    }, [tabSearch.defaultKeyword, setSearchDrawer]);

    const buttonProps = {
        size: 14,
        transparent: true,
        variables: () => ({
            buttonPadding: 10,
        }),
        className: css(S.MobileButtonFilters),
    } as ButtonProps;

    return (
        <>
            <div className={css(S.MobileFilterWarp)}>
                <Flex vAlign="center">
                    <Button
                        iconBefore={<Filter aria-label={'filter'} />}
                        onClick={() => setFilterDrawerState(true)}
                        {...buttonProps}
                    >
                        Filters
                    </Button>
                    {shopFilter && (
                        <Button
                            key={SHOP_FILTER.filterKey}
                            variant="text"
                            size={18}
                            variables={{
                                buttonPadding: pxToRem(10),
                                iconPadding: 0,
                                buttonSize18TextFontSize: pxToRem(14),
                                buttonSize18LineHeight: pxToRem(14),
                            }}
                            iconBefore={
                                shopFilter.checked ? (
                                    <CheckboxFill />
                                ) : (
                                    <CheckboxBlankLine />
                                )
                            }
                            disabled={shopFilter.disabled}
                            onClick={() => {
                                updateFilter(
                                    SHOP_FILTER.filterKey,
                                    shopFilter.newState,
                                );
                            }}
                            className={css(S.MobileButtonFilters)}
                        >
                            {shopFilter.label}
                        </Button>
                    )}
                </Flex>
                <Button
                    iconAfter={<SearchLine aria-label={'search'} />}
                    onClick={() => toggleSearchDrawer()}
                    {...buttonProps}
                >
                    Search
                </Button>
            </div>
            <AnimatePresence>
                {searchDrawerOpen && (
                    <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: pxToRem(57) }}
                        exit={{ height: 0 }}
                        className={css(S.MobileSearchDrawer)}
                    >
                        <InPageSearchBar
                            {...tabSearch}
                            toggleSearchDrawer={() => toggleSearchDrawer()}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            <FilterDrawer
                open={filterDrawerOpen}
                onOpenChange={(_, data) => setFilterDrawerState(!!data)}
                filterSections={filters ?? []}
                filterStates={filterStates}
                category={category}
                onApplyFilters={(_, data) => {
                    setFilterDrawerState(false);
                    resetFilters(data);
                }}
                ignoreFilterKeys={ignoreFilterKeys}
            />
        </>
    );
};
